import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Power } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Input } from "@/components/ui/Input.tsx";
import { Select } from "@/components/ui/Select.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Modal } from "@/components/ui/Modal.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { promosApi, type PromoCode } from "@/api/promos.ts";

interface PromoForm {
  code: string;
  discountType: "percent" | "fixed";
  discountValue: string;
  maxRedemptions: string;
  expiresAt: string;
}

const emptyForm: PromoForm = { code: "", discountType: "percent", discountValue: "", maxRedemptions: "", expiresAt: "" };

export function AdminPromosPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<PromoForm>(emptyForm);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "promos"],
    queryFn: () => promosApi.adminList(),
  });

  const create = useMutation({
    mutationFn: () =>
      promosApi.adminCreate({
        code: form.code,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : undefined,
        expiresAt: form.expiresAt || undefined,
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "promos"] }); setShowCreate(false); setForm(emptyForm); },
  });

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      promosApi.adminUpdate(id, { isActive: !isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "promos"] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => promosApi.adminDelete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "promos"] }); setDeleteId(null); },
  });

  const promos: PromoCode[] = data?.promos ?? [];

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-4xl p-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-surface-900">Promo Codes</h1>
        <Button size="sm" onClick={() => { setForm(emptyForm); setShowCreate(true); }}>
          <Plus className="w-4 h-4 mr-1" /> New Code
        </Button>
      </div>

      <div className="space-y-3">
        {promos.length === 0 && (
          <Card><CardContent className="py-8 text-center text-surface-400 text-sm">No promo codes yet.</CardContent></Card>
        )}
        {promos.map((p) => {
          const expired = p.expiresAt && new Date(p.expiresAt) < new Date();
          const maxedOut = p.maxRedemptions !== null && p.currentRedemptions >= p.maxRedemptions;
          return (
            <Card key={p.id}>
              <CardContent className="py-4 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono font-bold text-sm text-surface-900">{p.code}</span>
                    <Badge variant={p.isActive && !expired && !maxedOut ? "success" : "muted"}>
                      {!p.isActive ? "Inactive" : expired ? "Expired" : maxedOut ? "Maxed" : "Active"}
                    </Badge>
                    <Badge variant="info">
                      {p.discountType === "percent" ? `${p.discountValue}% off` : `$${(p.discountValue / 100).toFixed(2)} off`}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-surface-500">
                    <span>{p.currentRedemptions}{p.maxRedemptions !== null ? `/${p.maxRedemptions}` : ""} used</span>
                    {p.expiresAt && <span>Expires {new Date(p.expiresAt).toLocaleDateString()}</span>}
                    {p.stripeCouponId && <span className="font-mono">Stripe: {p.stripeCouponId}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => toggle.mutate({ id: p.id, isActive: p.isActive })} title={p.isActive ? "Deactivate" : "Activate"}>
                    <Power className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" aria-label="Delete this promo" onClick={() => setDeleteId(p.id)}>
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New Promo Code">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-surface-600 mb-1">Code</label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="LAUNCH20" className="font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-surface-600 mb-1">Discount Type</label>
              <Select value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value as "percent" | "fixed" })} options={[{value:"percent",label:"Percentage"},{value:"fixed",label:"Fixed amount (cents)"}]} />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-600 mb-1">Value</label>
              <Input type="number" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} placeholder={form.discountType === "percent" ? "20" : "500"} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-surface-600 mb-1">Max Redemptions (optional)</label>
              <Input type="number" value={form.maxRedemptions} onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })} placeholder="100" />
            </div>
            <div>
              <label className="block text-xs font-medium text-surface-600 mb-1">Expires At (optional)</label>
              <Input type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !form.code || !form.discountValue}>
            {create.isPending ? <Spinner className="w-4 h-4" /> : "Create"}
          </Button>
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal open={deleteId !== null} onClose={() => setDeleteId(null)} title="Delete Promo Code" size="sm">
        <p className="text-sm text-surface-600 mb-4">Are you sure? This will permanently delete the code and its redemption history.</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => deleteId && remove.mutate(deleteId)} disabled={remove.isPending}>
            {remove.isPending ? <Spinner className="w-4 h-4" /> : "Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
